import React from 'react';
import FhirSearchService from '@lifeomic/phc-web-toolkit/src/services/FhirSearchService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks';
import useToggle from '@lifeomic/phc-web-toolkit/src/hooks/useToggle';
import { buildHistogramQuery, buildHistogramLimitsQuery } from './queries';
import { FilterTimes } from './types';

const fhirSearchService = new FhirSearchService();

export type HistogramData = {
  date: Array<string>;
  number: Array<string>;
};

type useLoadHistogramDataReturnValue = {
  histogramData: HistogramData | null;
  maxTime: number | null;
  isLoading: boolean;
  error: boolean;
};

const times = {
  year: 31556952000,
  month: 2592000000,
  week: 604800000,
  day: 86400000,
  hour: 3600000,
  minute: 60000,
  second: 1000,
};

const findHistogramAggregationSpan = (minTime: number, maxTime: number) => {
  const millisecondDiff = maxTime + times.second - minTime;
  if (millisecondDiff / times.second <= 180) return 'second';
  else if (millisecondDiff / times.minute <= 180) return 'minute';
  else if (millisecondDiff / times.hour <= 72) return 'hour';
  else if (millisecondDiff / times.day <= 28) return 'day';
  else if (millisecondDiff / times.week <= 16) return 'week';
  else if (millisecondDiff / times.month <= 36) return 'month';
  return 'year';
};

export const useLoadHistogramData = (
  searchValue: string,
  filterTimes: FilterTimes,
): useLoadHistogramDataReturnValue => {
  const [queryProject] = useService(fhirSearchService.search);
  const activeProjectId = useActiveProjectId();

  const [histogramData, setHistogramData] =
    React.useState<HistogramData | null>(null);
  const [maxTime, setMaxTime] = React.useState<number | null>(null);
  const [error, setError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading, setLoadingFinished] = useToggle(true);

  const fetchHistogramData = React.useCallback(async () => {
    setIsLoading();

    try {
      const histogramLimitsResponse = await queryProject(
        activeProjectId,
        buildHistogramLimitsQuery(searchValue, filterTimes),
      );
      const aggregations = histogramLimitsResponse?.data?.aggregations;
      const minTime = aggregations?.minDate?.value;
      const maxTime = aggregations?.maxDate?.value;
      setMaxTime(maxTime + 1000);
      const interval = findHistogramAggregationSpan(minTime, maxTime);
      const bucketsResponse = await queryProject(
        activeProjectId,
        buildHistogramQuery(
          searchValue,
          maxTime,
          minTime,
          interval,
          (-(new Date().getTimezoneOffset() / 60)).toString(),
        ),
      );
      const buckets = bucketsResponse?.data?.aggregations?.results?.buckets;
      setHistogramData({
        date: buckets?.map((_: any) => _.key_as_string) || [],
        number: buckets?.map((_: any) => _.doc_count) || [],
      });
    } catch (e) {
      console.error(e);
      setError(true);
    } finally {
      setLoadingFinished();
    }
  }, [
    searchValue,
    filterTimes,
    activeProjectId,
    queryProject,
    setIsLoading,
    setLoadingFinished,
  ]);

  React.useEffect(() => {
    if (!activeProjectId) return;

    setHistogramData(null);
    fetchHistogramData();
  }, [activeProjectId, fetchHistogramData]);

  return {
    histogramData,
    maxTime,
    isLoading,
    error,
  };
};
